import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import clsx from "clsx";
import { HttpStatusCode } from "axios";

import { TermAgreed, UserAlarm } from "../../../types/member";

import { MANAGER_ROLE } from "../../../consts/member";

import { getLocalValue, removeLocalValue } from "../../../libs/localStorage";
import { setLongTermToken } from "../../../libs/token";
import { getDateString } from "../../../libs/dateFormat";
import { safeReplaceAll } from "../../../libs/replace";

import { deleteLongTermToken, logout } from "../../../services/authServices";
import { getMemberInfo } from "../../../services/memberServices";
import { updateUserPush } from "../../../services/pushServices";
import {
  PublicTermPurpose,
  getPublicTerms,
  patchAgreementTerms,
} from "../../../services/etcServices";

import { useUserContext } from "../../../contexts/userContext";

import useSimplePopup from "../../../hooks/useSimplePopup";
import useFullScreenLoading from "../../../hooks/useFullScreenLoading";

import StickyHeader from "../../../components/Layout/StickyHeader";
import CommonSwitch from "../../../components/Input/Switch";
import HorizontalRule from "../../../components/Rule/Horizontal";
import CommonPopup from "../../../components/Popup";
import CommonButton from "../../../components/Button";
import WithDrawPopup from "../../../components/Popup/WithDraw";
import TermsPopup from "../../../components/Popup/Terms";

type Props = {};

export default function MyInfoConfig(props: Props) {
  const navigate = useNavigate();
  const [autoLogin, setAutoLogin] = React.useState<boolean>(
    getLocalValue("AUTO") !== null
  );
  const [popup, setPopup] = React.useState<
    "logout" | "withdraw" | "showTerms" | ""
  >("");
  const [user, { resetUser, updateField }] = useUserContext();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const loadingComponent = useFullScreenLoading({ isLoading });
  const [termType, setTermType] = useState<PublicTermPurpose | "">("");
  const [simplePopupComponent, { open }] = useSimplePopup({});

  const loadMemberInfo = async () => {
    try {
      const res = await getMemberInfo();
      if (res.status === HttpStatusCode.Ok) {
        updateField({ ...res.data });
      } else {
        console.log("MyInfo getMemberInfo status", res.status);
      }
    } catch (e: any) {
      if (e?.response?.status === HttpStatusCode.Unauthorized) {
        logout();
      } else if (e?.response?.data?.message) {
        open?.(e?.response?.data?.message.replace(". ", ".\n"));
      } else {
        open?.("내정보 조회에 실패하였습니다.");
      }
    }
  };

  useEffect(() => {
    loadMemberInfo();
  }, []);

  return (
    <div>
      {loadingComponent}
      <StickyHeader
        title="설정"
        onBackClick={() => navigate("/myinfo")}
        className="px-5"
      />
      <MyInfoConfigRow disabled={!autoLogin}>
        <div>자동 로그인</div>
        <CommonSwitch
          value={autoLogin}
          onClick={async (value: boolean) => {
            if (value) {
              setIsLoading(true);
              setLongTermToken("AUTO");
              setIsLoading(false);
            } else {
              try {
                deleteLongTermToken({
                  Scope: "AUTO",
                  Token: getLocalValue("AUTO") ?? "",
                });
              } catch (e: any) {
                if (e?.response?.data?.error) {
                  open(e.response.data.error);
                } else {
                  open("자동 로그인 설정 해제에 실패했습니다.");
                }
              }
              removeLocalValue("AUTO");
            }
            setAutoLogin(value);
          }}
          variant="black"
        />
      </MyInfoConfigRow>
      <MyInfoConfigRow onClick={() => navigate("/myinfo/config/push/user")}>
        서비스 이용알림 수신 설정
      </MyInfoConfigRow>
      {!user.role?.includes(MANAGER_ROLE.USER) && (
        <MyInfoConfigRow
          onClick={() => navigate("/myinfo/config/push/manager")}
        >
          서비스 이용알림 수신 설정(매니저)
        </MyInfoConfigRow>
      )}
      <MyInfoConfigRow>
        마케팅 처리 방침 설정
        <CommonSwitch
          value={
            user.memberAgreedTermsList?.find(
              (item) => item.purpose === "marketing"
            )
              ? true
              : false
          }
          onClick={() => {
            const currentAgreed = user.memberAgreedTermsList?.find(
              (item) => item.purpose === "marketing"
            )
              ? true
              : false;

            getPublicTerms("marketing").then((res) => {
              if (res.data.termsId) {
                patchAgreementTerms(res.data.termsId, {
                  agreement: !currentAgreed,
                })
                  .then((res) => {
                    if (res.status === HttpStatusCode.Ok) {
                      updateField({
                        memberAgreedTermsList: [
                          ...(user.memberAgreedTermsList?.filter(
                            (item) => item.purpose !== "marketing"
                          ) ?? []),
                          ...(!currentAgreed
                            ? [
                                {
                                  purpose: "marketing",
                                  termsId: res.data.termsId,
                                } as TermAgreed,
                              ]
                            : []),
                        ],
                      });
                      open(
                        `${safeReplaceAll(
                          getDateString(new Date()),
                          "-",
                          "."
                        )}에 마케팅 처리 방침을 ${
                          !currentAgreed ? "동의" : "철회"
                        } 하였습니다.`
                      );
                      loadMemberInfo();
                    } else {
                      open(
                        `${safeReplaceAll(
                          getDateString(new Date()),
                          "-",
                          "."
                        )}에 마케팅 처리 방침 ${
                          !currentAgreed ? "동의" : "철회"
                        }에 실패했습니다.\n다시 한번 시도해주세요.`
                      );
                    }
                  })
                  .catch((err) => {
                    open(
                      `${safeReplaceAll(
                        getDateString(new Date()),
                        "-",
                        "."
                      )}에 마케팅 처리 방침을 ${
                        !currentAgreed ? "동의" : "철회"
                      }에 실패했습니다.\n다시 한번 시도해주세요.`
                    );
                  });
              }
            });
          }}
          variant="black"
        />
      </MyInfoConfigRow>
      <MyInfoConfigRow>
        광고성 알림 수신 설정
        <CommonSwitch
          value={user.alarm?.advertise === true}
          onClick={() => {
            const advertise = !user.alarm?.advertise;
            updateUserPush({
              advertise,
            })
              .then((res) => {
                if (res.status === HttpStatusCode.Ok) {
                  updateField({
                    alarm: {
                      ...user.alarm,
                      advertise,
                    } as UserAlarm,
                  });
                  open(
                    `${safeReplaceAll(
                      getDateString(new Date()),
                      "-",
                      "."
                    )}에 광고성 알림 수신을 ${
                      advertise ? "동의" : "철회"
                    } 하였습니다.`
                  );
                  loadMemberInfo();
                } else {
                  open(
                    `${safeReplaceAll(
                      getDateString(new Date()),
                      "-",
                      "."
                    )}에 광고성 알림 수신을 ${
                      advertise ? "동의" : "철회"
                    }에 실패했습니다.\n다시 한번 시도해주세요.`
                  );
                }
              })
              .catch((err) => {
                open(
                  `${safeReplaceAll(
                    getDateString(new Date()),
                    "-",
                    "."
                  )}에 광고성 알림 수신을 ${
                    advertise ? "동의" : "철회"
                  }에 실패했습니다.\n다시 한번 시도해주세요.`
                );
              });
          }}
          variant="black"
        />
      </MyInfoConfigRow>
      <MyInfoConfigRow onClick={() => navigate("/myinfo/config/app/info")}>
        앱 정보
      </MyInfoConfigRow>
      <a href="https://pf.kakao.com/_xhJxkZK/chat">
        <MyInfoConfigRow>
          앱이용문의(kakao)
          <MyInfoConfigKakaoIcon />
        </MyInfoConfigRow>
      </a>

      <MyInfoConfigRow
        onClick={() => {
          setPopup("showTerms");
          setTermType("personal_gather");
        }}
      >
        개인정보 수집 및 이용
      </MyInfoConfigRow>
      <MyInfoConfigRow
        onClick={() => {
          setPopup("showTerms");
          setTermType("service");
        }}
      >
        서비스 이용약관
      </MyInfoConfigRow>
      <MyInfoConfigRow
        onClick={() => {
          setPopup("showTerms");
          setTermType("location");
        }}
      >
        위치기반서비스 이용약관
      </MyInfoConfigRow>
      <MyInfoConfigRow
        onClick={() => {
          setPopup("showTerms");
          setTermType("personal_process");
        }}
        className="font-semibold"
      >
        개인정보 처리방침
      </MyInfoConfigRow>
      <MyInfoConfigRow onClick={() => setPopup("logout")}>
        로그아웃
      </MyInfoConfigRow>
      <div
        className="flex justify-end px-5 h-bottom-button text-brand-neutral-40 text-sm items-center underline"
        onClick={() => setPopup("withdraw")}
      >
        탈퇴하기
      </div>
      <CommonPopup
        isOpen={popup === "logout"}
        title="로그아웃"
        onClose={() => setPopup("")}
        padding={0}
      >
        <div className="flex flex-col items-center">
          <div className="p-5 pt-0">
            <div className="flex flex-col gap-2.5 py-2.5 items-center text-center whitespace-pre-line text-brand-neutral-60 text-sm">
              <MyInfoConfigLogoutIcon />
              지금 로그아웃하시겠어요?
            </div>
          </div>
          <div className="flex w-full">
            <CommonButton
              variant="secondary"
              className="rounded-none h-bottom-button flex-1"
              onClick={() => setPopup("")}
            >
              취소
            </CommonButton>
            <CommonButton
              className="rounded-none h-bottom-button flex-1"
              onClick={() => {
                logout();
                resetUser();
                navigate("/auth/login");
              }}
            >
              로그아웃
            </CommonButton>
          </div>
        </div>
      </CommonPopup>

      <WithDrawPopup popup={popup} onClose={() => setPopup("")} />
      <TermsPopup
        popup={popup}
        type={termType as PublicTermPurpose}
        onClose={() => setPopup("")}
      />
      {simplePopupComponent}
    </div>
  );
}

export type MyInfoConfigRowProps = {
  children?: React.ReactNode;
  className?: string;
  onClick?: () => void;
  disabled?: boolean;
  subChildren?: React.ReactNode;
};

export const MyInfoConfigRow = ({
  children,
  className,
  onClick,
  disabled,
  subChildren,
}: MyInfoConfigRowProps) => {
  const childrenArray = React.Children.toArray(children);

  return (
    <>
      <div className="flex flex-col">
        <div
          className={clsx([
            "px-5 flex justify-between items-center h-[60px]",
            className,
            { "opacity-50": disabled },
          ])}
          onClick={onClick}
        >
          {children}
          {childrenArray.length <= 1 ? <MyInfoConfigRightArrow /> : null}
        </div>
        {subChildren}
      </div>
      <HorizontalRule margin="0" />
    </>
  );
};

export const MyInfoConfigLogoutIcon = () => (
  <svg
    width="60"
    height="60"
    viewBox="0 0 60 60"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 9L46 9V51H12L12 9Z"
      fill="white"
      stroke="black"
      strokeWidth="2"
    />
    <rect x="34" y="8" width="3" height="44" fill="white" />
    <path
      d="M8 8.80217L33 3.24662L33 56.7534L8 51.1978L8 8.80217Z"
      fill="white"
      stroke="black"
      strokeWidth="2"
    />
    <path
      d="M38.7917 27.5C37.2183 27.5 36.5 28.9649 36.5 30C36.5 31.0351 37.2183 32.5 38.7917 32.5H56.2083C57.7817 32.5 58.5 31.0351 58.5 30C58.5 28.9649 57.7817 27.5 56.2083 27.5H38.7917Z"
      fill="black"
      stroke="white"
      strokeWidth="3"
      strokeLinecap="round"
    />
    <path
      d="M52 23L58 30"
      stroke="black"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      d="M52 37L58 30"
      stroke="black"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

export const MyInfoConfigKakaoIcon = () => (
  <svg
    width="36"
    height="36"
    viewBox="0 0 36 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="18" cy="18" r="18" fill="#FEE500" />
    <path
      d="M17.9918 8C11.6303 8 6.5 12.1042 6.5 17.0867C6.5 20.3208 8.63419 23.1527 11.8355 24.7698L10.752 28.8165C10.7315 28.8772 10.7284 28.9423 10.7429 29.0046C10.7574 29.067 10.7889 29.124 10.834 29.1695C10.8998 29.2274 10.9844 29.2595 11.0721 29.2597C11.1448 29.2539 11.2138 29.2252 11.2691 29.1777L15.9315 26.0339C16.6197 26.1289 17.3135 26.1782 18.0082 26.1816C24.3615 26.1816 29.5 22.0774 29.5 17.0867C29.5 12.096 24.3451 8 17.9918 8Z"
      fill="#392020"
    />
    <path
      d="M10.5219 15.6495H9.26605C9.11039 15.654 8.95665 15.6141 8.82279 15.5346C8.76336 15.4973 8.71227 15.4482 8.67272 15.3903C8.63317 15.3324 8.60601 15.2669 8.59296 15.198C8.58897 15.1543 8.58897 15.1104 8.59296 15.0667C8.58906 14.985 8.60492 14.9035 8.63922 14.8292C8.67351 14.7549 8.72522 14.6899 8.78996 14.6399C8.93306 14.5362 9.10579 14.4815 9.28246 14.4839H13.0665C13.2234 14.4784 13.3782 14.5214 13.5098 14.607C13.5706 14.6426 13.6228 14.6913 13.6625 14.7495C13.7023 14.8077 13.7286 14.874 13.7396 14.9436C13.7436 14.9845 13.7436 15.0258 13.7396 15.0667C13.7435 15.1497 13.7277 15.2323 13.6934 15.3079C13.6592 15.3835 13.6075 15.4499 13.5426 15.5017C13.403 15.6052 13.232 15.6574 13.0583 15.6495H11.8435V19.9671C11.8484 20.0591 11.8342 20.1511 11.8017 20.2373C11.7692 20.3235 11.7191 20.402 11.6547 20.4678C11.5929 20.5308 11.5186 20.5801 11.4366 20.6126C11.3546 20.6451 11.2667 20.6601 11.1786 20.6566C11.0244 20.661 10.8736 20.6118 10.7518 20.5171C10.6358 20.4247 10.5571 20.2935 10.5301 20.1477C10.522 20.0878 10.522 20.0271 10.5301 19.9671L10.5219 15.6495Z"
      fill="#FEE500"
    />
    <path
      d="M14.1017 14.8046C14.1378 14.6695 14.2226 14.5525 14.3397 14.4762C14.4645 14.4055 14.6068 14.3713 14.7501 14.3777H15.0621C15.2142 14.3723 15.3648 14.4092 15.4971 14.4844C15.6373 14.5787 15.7369 14.7223 15.7762 14.8866L17.4179 19.5244C17.4604 19.6454 17.4961 19.7688 17.5246 19.8938C17.5274 19.9458 17.5274 19.9978 17.5246 20.0498C17.5274 20.1298 17.5126 20.2094 17.4814 20.2831C17.4503 20.3569 17.4033 20.4229 17.344 20.4766C17.2865 20.5356 17.2174 20.582 17.1412 20.6131C17.0649 20.6442 16.9831 20.6591 16.9007 20.6572C16.7741 20.6698 16.647 20.6394 16.5398 20.5709C16.4326 20.5024 16.3515 20.3998 16.3097 20.2796L15.965 19.27H13.8062L13.4614 20.2796C13.4206 20.4022 13.3386 20.5068 13.2294 20.5756C13.1201 20.6445 12.9903 20.6733 12.8622 20.6572C12.7234 20.6608 12.5874 20.6174 12.4764 20.534C12.3682 20.448 12.2952 20.3254 12.2712 20.1893C12.2668 20.1429 12.2668 20.0962 12.2712 20.0498C12.2571 19.9683 12.2571 19.885 12.2712 19.8036C12.2712 19.7133 12.3287 19.6147 12.3615 19.5244L14.1017 14.8046ZM14.9225 15.8799L14.1427 18.3424H15.6941L14.9225 15.8799Z"
      fill="#FEE500"
    />
    <path
      d="M17.7131 15.0672C17.7048 14.883 17.7697 14.703 17.8937 14.5665C17.9567 14.5029 18.0324 14.4532 18.1158 14.4207C18.1992 14.3882 18.2886 14.3735 18.378 14.3776C18.5318 14.3758 18.6818 14.4249 18.8048 14.5172C18.9188 14.6135 18.9946 14.7474 19.0183 14.8948C19.0263 14.9519 19.0263 15.01 19.0183 15.0672V19.3849H21.2756C21.432 19.3828 21.5858 19.4254 21.7188 19.5079C21.7786 19.5447 21.8299 19.5938 21.8695 19.6518C21.9091 19.7098 21.9361 19.7754 21.9487 19.8445C21.9487 19.8445 21.9487 19.9266 21.9487 19.9676C21.9526 20.0494 21.9367 20.1308 21.9024 20.2051C21.8681 20.2794 21.8164 20.3444 21.7517 20.3945C21.6086 20.4981 21.4358 20.5528 21.2592 20.5504H18.4847C18.308 20.5618 18.1324 20.5158 17.984 20.4191C17.8513 20.3199 17.7628 20.1725 17.7377 20.0087C17.7317 19.9185 17.7317 19.828 17.7377 19.7378L17.7131 15.0672Z"
      fill="#FEE500"
    />
    <path
      d="M22.0962 15.0676C22.0913 14.8839 22.1558 14.7052 22.2768 14.5669C22.3998 14.4551 22.5576 14.3891 22.7236 14.3801C22.8896 14.3712 23.0536 14.4197 23.1879 14.5176C23.3045 14.613 23.383 14.7469 23.4096 14.8952C23.4136 14.9526 23.4136 15.0102 23.4096 15.0676V17.0786L25.4863 14.6817C25.5663 14.6 25.6513 14.5233 25.7408 14.452C25.8251 14.4018 25.9218 14.3761 26.0199 14.378C26.1633 14.3772 26.3036 14.4202 26.4221 14.5012C26.4779 14.5388 26.5255 14.5872 26.5622 14.6437C26.5989 14.7001 26.6238 14.7633 26.6355 14.8295C26.6388 14.8458 26.6388 14.8626 26.6355 14.8788C26.6315 14.8978 26.6315 14.9173 26.6355 14.9362C26.6352 15.0226 26.6126 15.1075 26.5698 15.1825C26.5263 15.262 26.474 15.3363 26.4139 15.4041L25.0513 16.8981L26.6929 19.4427V19.5247C26.7782 19.6424 26.8446 19.7726 26.8899 19.9106V19.9516C26.8994 20.0467 26.8857 20.1427 26.85 20.2313C26.8142 20.32 26.7575 20.3986 26.6847 20.4605C26.5478 20.564 26.3803 20.6189 26.2087 20.6165C26.0946 20.6212 25.9813 20.5957 25.8803 20.5426C25.771 20.4692 25.6809 20.3707 25.6176 20.2553L24.0909 17.7928L23.3603 18.5562V19.9269C23.3652 20.1106 23.3008 20.2894 23.1797 20.4276C23.1149 20.491 23.0377 20.5406 22.953 20.573C22.8683 20.6055 22.7779 20.6203 22.6872 20.6165C22.5358 20.6197 22.3879 20.5703 22.2686 20.4769C22.1527 20.3845 22.074 20.2534 22.047 20.1076C22.0389 20.0477 22.0389 19.9869 22.047 19.9269L22.0962 15.0676Z"
      fill="#FEE500"
    />
  </svg>
);

export const MyInfoConfigRightArrow = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9 18L15 12L9 6"
      stroke="black"
      strokeWidth="1.75"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
  </svg>
);
